import { OTOKOC_CATALOG_URL_ENTRIES } from './catalogUrlLookup.js';

export type OtokocPageContext = {
  pageType: 'pdp' | 'plp' | 'home' | 'cart' | 'search' | 'other';
  sku?: string;
  skuList?: string[];
  categoryTree?: string[];
  url?: string;
  extra?: Record<string, unknown>;
};

type BrandInfo = {
  slug: string;
  label: string;
};

const BRAND_LABELS = new Map<string, string>([
  ['ford', 'Ford'],
  ['volvo', 'Volvo'],
  ['fiat', 'Fiat'],
  ['jeep', 'Jeep'],
  ['alfa-romeo', 'Alfa Romeo'],
  ['maserati', 'Maserati'],
  ['opel', 'Opel'],
  ['peugeot', 'Peugeot'],
  ['citroen', 'Citroen'],
]);

const VEHICLE_LINK_LIMIT = 12;
const CONTEXT_SKU_LIST_LIMIT = 48;
const CATALOG_BY_PATH = new Map(
  OTOKOC_CATALOG_URL_ENTRIES.map((entry) => [normalizeCatalogPath(entry.url), entry])
);
const CATALOG_SKUS: ReadonlySet<string> = new Set(
  OTOKOC_CATALOG_URL_ENTRIES.map((entry) => entry.sku)
);

type AnalyticsContext = {
  pageType?: OtokocPageContext['pageType'];
  sku?: string;
  skuList: string[];
  categoryTree?: string[];
};

declare global {
  interface Window {
    __OTOKOC_KIT_PAGE_CONTEXT__?: OtokocPageContext;
  }
}

export function detectOtokocPageContext(href?: string): OtokocPageContext {
  const url = resolveUrl(href ?? currentHref());
  if (!url) {
    return { pageType: 'other' };
  }

  const segments = pathSegments(url);
  const brand = brandFromSegment(segments[0]);
  const routeCategoryTree = brand ? categoryTreeFromRoute(brand, segments) : undefined;
  const canonicalUrl = readCanonicalUrl();
  const canonicalCatalogEntry = canonicalUrl ? catalogEntryForHref(canonicalUrl, url) : undefined;
  const routeCatalogEntry = catalogEntryForUrl(url) ?? canonicalCatalogEntry;
  const analytics = readAnalyticsContext();
  const productLinks = collectVehicleLinkSkus(url);
  const structuredProduct = readStructuredProduct();
  const domSku = readDomSku();
  const routePrefixSkus = brand ? collectCatalogSkusForRoutePrefix(segments) : [];
  const title = readDocumentTitle();
  const pageSku = firstKnownSku(
    analytics.sku,
    domSku,
    structuredProduct?.sku,
    routeCatalogEntry?.sku
  );
  const visibleSkus = uniqueStrings([...analytics.skuList, ...productLinks]).slice(
    0,
    CONTEXT_SKU_LIST_LIMIT
  );
  const commonExtra = compactRecord({
    title,
    canonical_url: canonicalUrl && canonicalUrl !== url.href ? canonicalUrl : undefined,
    analytics_page_type: analytics.pageType,
  });

  if (isHomePath(segments)) {
    return compactContext({
      pageType: 'home',
      skuList: visibleSkus,
      url: url.href,
      extra: compactRecord({
        ...commonExtra,
        visible_skus: visibleSkus,
      }),
    });
  }

  if (isSearchPath(url, segments)) {
    return compactContext({
      pageType: 'search',
      skuList: visibleSkus,
      categoryTree: analytics.categoryTree,
      url: url.href,
      extra: compactRecord({
        ...commonExtra,
        search_query: url.searchParams.get('q') || url.searchParams.get('search') || undefined,
        visible_skus: visibleSkus,
      }),
    });
  }

  if (
    analytics.pageType === 'pdp' ||
    routeCatalogEntry ||
    structuredProduct ||
    hasPdpDomSignals()
  ) {
    return compactContext({
      pageType: 'pdp',
      sku: pageSku,
      categoryTree: routeCategoryTree ?? structuredProduct?.categoryTree ?? analytics.categoryTree,
      url: url.href,
      extra: compactRecord({
        ...commonExtra,
        brand: brand?.label,
        model: segments[1] ? formatSlugLabel(segments[1]) : undefined,
        trim: segments[2] ? formatSlugLabel(segments[2]) : undefined,
        product_name: structuredProduct?.name,
        product_slug: brand && segments.length >= 3 ? segments.slice(0, 3).join('/') : undefined,
        visible_skus: visibleSkus,
      }),
    });
  }

  if (
    brand &&
    (segments.length === 1 ||
      analytics.pageType === 'plp' ||
      visibleSkus.length >= 2 ||
      routePrefixSkus.length > 0)
  ) {
    return detectBrandPageContext({
      brand,
      segments,
      url,
      visibleSkus,
      routePrefixSkus,
      commonExtra,
    });
  }

  if (analytics.pageType === 'plp' || visibleSkus.length >= 2) {
    return compactContext({
      pageType: 'plp',
      skuList: visibleSkus,
      categoryTree: analytics.categoryTree,
      url: url.href,
      extra: compactRecord({
        ...commonExtra,
        visible_skus: visibleSkus,
      }),
    });
  }

  return compactContext({
    pageType: 'other',
    url: url.href,
    extra: commonExtra,
  });
}

export function exposeOtokocPageContext(context: OtokocPageContext): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.__OTOKOC_KIT_PAGE_CONTEXT__ = context;

  if (typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
    window.dispatchEvent(new CustomEvent('otokoc-kit:page-context', { detail: context }));
  }
}

function detectBrandPageContext(input: {
  brand: BrandInfo;
  segments: string[];
  url: URL;
  visibleSkus: string[];
  routePrefixSkus: string[];
  commonExtra?: Record<string, unknown>;
}): OtokocPageContext {
  const { brand, segments, url, visibleSkus, routePrefixSkus, commonExtra } = input;
  const model = segments[1];
  const skuList = uniqueStrings([...visibleSkus, ...routePrefixSkus]).slice(
    0,
    CONTEXT_SKU_LIST_LIMIT
  );
  // The brand/model route gives a clean "Ford > Focus" tree; prefer it over the
  // flatter analytics category list (e.g. "Sedan, Hatchback").
  const categoryTree = categoryTreeFromRoute(brand, segments);

  return compactContext({
    pageType: 'plp',
    skuList,
    categoryTree,
    url: url.href,
    extra: compactRecord({
      ...commonExtra,
      brand: brand.label,
      model: model ? formatSlugLabel(model) : undefined,
      visible_skus: skuList,
    }),
  });
}

function currentHref(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.location?.href;
}

function resolveUrl(href?: string): URL | null {
  if (!href) {
    return null;
  }

  try {
    return new URL(href, 'https://www.otokoc.com.tr/');
  } catch {
    return null;
  }
}

function pathSegments(url: URL): string[] {
  return url.pathname
    .split('/')
    .map((segment) => normalizeSlug(decodeURIComponent(segment)))
    .filter(Boolean);
}

function normalizeSlug(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/gu, '')
    .replace(/ı/gu, 'i')
    .replace(/[^a-z0-9-]+/gu, '-')
    .replace(/^-+|-+$/gu, '');
}

function brandFromSegment(segment?: string): BrandInfo | null {
  if (!segment) {
    return null;
  }

  const slug = normalizeSlug(segment);
  const label = BRAND_LABELS.get(slug);
  return label ? { slug, label } : null;
}

function isHomePath(segments: string[]): boolean {
  return (
    segments.length === 0 ||
    (segments.length === 1 && ['anasayfa', 'tr', 'tr-tr'].includes(segments[0]))
  );
}

function isSearchPath(url: URL, segments: string[]): boolean {
  if (url.searchParams.has('q') || url.searchParams.has('search')) {
    return true;
  }

  return segments.some((segment) => ['arama', 'search'].includes(segment));
}

function categoryTreeFromRoute(brand: BrandInfo, segments: string[]): string[] {
  return [
    brand.label,
    segments[1] ? formatSlugLabel(segments[1]) : undefined,
    segments[2] ? formatSlugLabel(segments[2]) : undefined,
  ].filter(Boolean) as string[];
}

function collectVehicleLinkSkus(baseUrl: URL): string[] {
  if (typeof document === 'undefined' || typeof document.querySelectorAll !== 'function') {
    return [];
  }

  const skus: string[] = [];
  const seen = new Set<string>();
  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href]'));

  for (const link of links) {
    const href = link.getAttribute('href');
    if (!href) {
      continue;
    }

    const url = resolveUrlAgainstBase(href, baseUrl);
    if (!url || !isOtokocHost(url.hostname)) {
      continue;
    }

    const entry = catalogEntryForUrl(url);
    if (!entry) {
      continue;
    }

    const sku = entry.sku;
    if (seen.has(sku)) {
      continue;
    }

    seen.add(sku);
    skus.push(sku);

    if (skus.length >= VEHICLE_LINK_LIMIT) {
      break;
    }
  }

  return skus;
}

function collectCatalogSkusForRoutePrefix(segments: string[]): string[] {
  if (segments.length === 0 || !brandFromSegment(segments[0])) {
    return [];
  }

  const prefix = pathFromSegments(segments.slice(0, 2));
  const skus: string[] = [];
  for (const entry of OTOKOC_CATALOG_URL_ENTRIES) {
    const path = normalizeCatalogPath(entry.url);
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      skus.push(entry.sku);
    }

    if (skus.length >= CONTEXT_SKU_LIST_LIMIT) {
      break;
    }
  }

  return skus;
}

function resolveUrlAgainstBase(href: string, baseUrl: URL): URL | null {
  try {
    return new URL(href, baseUrl.href);
  } catch {
    return null;
  }
}

function isOtokocHost(hostname: string): boolean {
  return hostname === 'otokoc.com.tr' || hostname.endsWith('.otokoc.com.tr');
}

function catalogEntryForHref(href: string, baseUrl?: URL) {
  const url = baseUrl ? resolveUrlAgainstBase(href, baseUrl) : resolveUrl(href);
  return url ? catalogEntryForUrl(url) : undefined;
}

function catalogEntryForUrl(url: URL) {
  return CATALOG_BY_PATH.get(normalizeCatalogPath(url.pathname));
}

function normalizeCatalogPath(pathOrUrl: string): string {
  let pathname = pathOrUrl;
  try {
    pathname = new URL(pathOrUrl, 'https://www.otokoc.com.tr/').pathname;
  } catch {
    // Treat non-URL input as a path.
  }

  return pathFromSegments(
    pathname
      .split('/')
      .map((segment) => normalizeSlug(decodeURIComponent(segment)))
      .filter(Boolean)
  );
}

function pathFromSegments(segments: string[]): string {
  return `/${segments.map(normalizeSlug).filter(Boolean).join('/')}`;
}

function firstKnownSku(...values: Array<string | undefined>): string | undefined {
  for (const value of values) {
    if (value && CATALOG_SKUS.has(value)) {
      return value;
    }
  }

  return undefined;
}

function uniqueStrings(values: Array<string | undefined>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = typeof value === 'string' ? value.trim() : '';
    if (!normalized || seen.has(normalized)) {
      continue;
    }

    seen.add(normalized);
    result.push(normalized);
  }

  return result;
}

function readAnalyticsContext(): AnalyticsContext {
  if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) {
    return { skuList: [] };
  }

  const pageTypes: OtokocPageContext['pageType'][] = [];
  const skus: string[] = [];
  const categories: string[] = [];

  for (const entry of window.dataLayer) {
    collectAnalyticsSignals(entry, { pageTypes, skus, categories });
  }

  return {
    pageType: pageTypes[0],
    sku: skus[0],
    skuList: uniqueStrings(skus).slice(0, CONTEXT_SKU_LIST_LIMIT),
    categoryTree: uniqueStrings(categories),
  };
}

function collectAnalyticsSignals(
  value: unknown,
  output: {
    pageTypes: OtokocPageContext['pageType'][];
    skus: string[];
    categories: string[];
  },
  depth = 0
): void {
  if (depth > 7 || value == null) {
    return;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      collectAnalyticsSignals(item, output, depth + 1);
    }
    return;
  }

  if (typeof value !== 'object') {
    return;
  }

  for (const [rawKey, entry] of Object.entries(value as Record<string, unknown>)) {
    const key = normalizeAnalyticsKey(rawKey);
    if (isPageTypeKey(key)) {
      const pageType = pageTypeFromUnknown(entry);
      if (pageType) {
        output.pageTypes.push(pageType);
      }
    }

    if (isSkuKey(key) || isProductUrlKey(key)) {
      const sku = knownSkuFromUnknown(entry);
      if (sku) {
        output.skus.push(sku);
      }
    }

    if (isCategoryKey(key)) {
      output.categories.push(...categoryStringsFromUnknown(entry));
    }

    collectAnalyticsSignals(entry, output, depth + 1);
  }
}

function normalizeAnalyticsKey(key: string): string {
  return key.replace(/[^a-z0-9]/giu, '').toLocaleLowerCase('en-US');
}

function isPageTypeKey(key: string): boolean {
  return [
    'pagetype',
    'pagecategory',
    'pagetemplate',
    'templatename',
    'contentgroup',
    'event',
  ].includes(key);
}

function isSkuKey(key: string): boolean {
  return [
    'sku',
    'itemid',
    'itemcode',
    'productid',
    'productcode',
    'productsku',
    'vehicleid',
    'vehiclesku',
  ].includes(key);
}

function isProductUrlKey(key: string): boolean {
  return ['url', 'href', 'link', 'producturl', 'detailurl', 'itemurl'].includes(key);
}

function isCategoryKey(key: string): boolean {
  // Only true category-hierarchy keys. itemcategory2/itemcategory3 on Otokoç carry
  // engine displacement and transmission noise (e.g. "1499, 999"), not a taxonomy.
  return ['category', 'categoryname', 'categorypath', 'categorytree', 'itemcategory'].includes(key);
}

function pageTypeFromUnknown(value: unknown): OtokocPageContext['pageType'] | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const normalized = normalizeSlug(value);
  if (!normalized) {
    return undefined;
  }

  if (/(basket|cart|sepet)/u.test(normalized)) {
    return 'cart';
  }
  if (/(search|arama)/u.test(normalized)) {
    return 'search';
  }
  if (/(home|homepage|ana-sayfa|anasayfa)/u.test(normalized)) {
    return 'home';
  }
  if (/(plp|listing|list|category|brand|product-list|urun-liste|liste)/u.test(normalized)) {
    return 'plp';
  }
  if (
    /(pdp|product-detail|productdetail|detail|detay|product-page|productpage|urun-detay)/u.test(
      normalized
    )
  ) {
    return 'pdp';
  }

  return undefined;
}

function knownSkuFromUnknown(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return firstKnownSku(String(value));
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const exactSku = firstKnownSku(trimmed);
  if (exactSku) {
    return exactSku;
  }

  const entry = catalogEntryForHref(trimmed);
  if (entry) {
    return entry.sku;
  }

  return undefined;
}

function categoryStringsFromUnknown(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap(categoryStringsFromUnknown);
  }

  if (typeof value !== 'string') {
    return [];
  }

  return value
    .split(/[>/|]/gu)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function hasPdpDomSignals(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const selectorMatches = (selector: string): boolean =>
    Boolean(document.querySelector?.(selector));

  if (selectorMatches('[class*="ProductDetail"],[class*="product-detail"]')) {
    return true;
  }

  if (selectorMatches('[id*="ProductDetail"],[id*="product-detail"]')) {
    return true;
  }

  const text = document.body?.innerText?.slice(0, 8000).toLocaleLowerCase('tr-TR') ?? '';
  return text.includes('teknik özellikler') && text.includes('öne çıkan özellikler');
}

function readDomSku(): string | undefined {
  if (typeof document === 'undefined' || typeof document.querySelectorAll !== 'function') {
    return undefined;
  }

  const selectors = [
    '[data-sku]',
    '[data-product-id]',
    '[data-productid]',
    '[data-product-code]',
    '[data-productcode]',
    'meta[property="product:retailer_item_id"]',
    'meta[name="product-id"]',
    'meta[name="sku"]',
  ];

  for (const element of Array.from(document.querySelectorAll<Element>(selectors.join(',')))) {
    const value =
      element.getAttribute('data-sku') ||
      element.getAttribute('data-product-id') ||
      element.getAttribute('data-productid') ||
      element.getAttribute('data-product-code') ||
      element.getAttribute('data-productcode') ||
      element.getAttribute('content');
    const sku = knownSkuFromUnknown(value);
    if (sku) {
      return sku;
    }
  }

  return undefined;
}

function readStructuredProduct():
  { name?: string; sku?: string; categoryTree?: string[] } | undefined {
  if (typeof document === 'undefined' || typeof document.querySelectorAll !== 'function') {
    return undefined;
  }

  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]')
  );
  for (const script of scripts) {
    const text = script.textContent?.trim();
    if (!text) {
      continue;
    }

    try {
      const match = findStructuredProduct(JSON.parse(text));
      if (match) {
        return match;
      }
    } catch {
      // Ignore malformed merchant JSON-LD.
    }
  }

  return undefined;
}

function findStructuredProduct(
  value: unknown
): { name?: string; sku?: string; categoryTree?: string[] } | undefined {
  if (Array.isArray(value)) {
    for (const item of value) {
      const match = findStructuredProduct(item);
      if (match) {
        return match;
      }
    }
    return undefined;
  }

  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  const type = record['@type'];
  const types = Array.isArray(type) ? type : [type];
  const isProduct = types.some((entry) => {
    const normalized = String(entry ?? '').toLocaleLowerCase('en-US');
    return normalized === 'product' || normalized === 'vehicle' || normalized === 'car';
  });

  if (isProduct) {
    return {
      name: typeof record.name === 'string' ? record.name : undefined,
      sku: knownSkuFromUnknown(record.sku),
      categoryTree: readStructuredCategoryTree(record),
    };
  }

  for (const child of Object.values(record)) {
    const match = findStructuredProduct(child);
    if (match) {
      return match;
    }
  }

  return undefined;
}

function readStructuredCategoryTree(record: Record<string, unknown>): string[] | undefined {
  const category = record.category;
  if (Array.isArray(category)) {
    return category.filter((entry): entry is string => typeof entry === 'string' && entry !== '');
  }

  if (typeof category === 'string' && category.trim()) {
    return category
      .split(/[>/|]/gu)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return undefined;
}

function readDocumentTitle(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const title = document.title?.trim();
  return title || undefined;
}

function readCanonicalUrl(): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const canonical = document.querySelector?.('link[rel="canonical"]')?.getAttribute('href')?.trim();
  return canonical || undefined;
}

function formatSlugLabel(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) =>
      part.length <= 2
        ? part.toLocaleUpperCase('tr-TR')
        : part[0].toLocaleUpperCase('tr-TR') + part.slice(1)
    )
    .join(' ');
}

function compactContext(context: OtokocPageContext): OtokocPageContext {
  const next = { ...context };

  if (next.sku === undefined) {
    delete next.sku;
  }

  if (next.skuList?.length === 0) {
    delete next.skuList;
  }

  if (next.categoryTree?.length === 0) {
    delete next.categoryTree;
  }

  if (next.extra && Object.keys(next.extra).length === 0) {
    delete next.extra;
  }

  return next;
}

function compactRecord(record: Record<string, unknown>): Record<string, unknown> | undefined {
  const entries = Object.entries(record).filter(([, value]) => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== undefined && value !== null && value !== '';
  });

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}
